import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const EXIT_TYPES = ["Resignation", "Retirement", "Termination", "Transfer"];
const EXIT_STATUSES = ["pending", "processing", "completed"];

const STATUS_TONES = {
  pending: "warning",
  processing: "info",
  completed: "success"
};

const EXIT_TYPE_ICONS = {
  Resignation: "exit_to_app",
  Retirement: "elderly",
  Termination: "cancel",
  Transfer: "swap_horiz"
};

const BLANK_FORM = {
  staffId: "",
  exitType: "Resignation",
  noticeDate: "",
  lastWorkingDay: "",
  status: "pending",
  notes: ""
};

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  infoMsg: { color: "#1565c0", fontSize: "12px", background: "#e3f2fd", borderRadius: "4px", padding: "8px 12px", marginBottom: "12px" }
};

class StaffExit extends Component {
  state = {
    exits: [],
    staff: [],
    loading: true,
    open: false,
    editing: null,
    form: { ...BLANK_FORM },
    saving: false,
    error: null,
    formError: null,
    apiUnavailable: false
  };

  componentDidMount() {
    this.fetchStaff();
    this.fetchExits();
  }

  fetchStaff = async () => {
    try {
      const res = await api.get("/staff");
      const d = res.data.data || res.data;
      this.setState({ staff: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  fetchExits = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await api.get("/hr/exits");
      const d = res.data.data || res.data;
      this.setState({ exits: Array.isArray(d) ? d : d.data || [], loading: false, apiUnavailable: false });
    } catch (e) {
      this.setState({ exits: [], loading: false, apiUnavailable: true });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  openAdd = () => {
    this.setState({ open: true, editing: null, form: { ...BLANK_FORM }, formError: null });
  };

  openEdit = exit_ => {
    this.setState({
      open: true,
      editing: exit_.id,
      formError: null,
      form: {
        staffId: exit_.staffId ? String(exit_.staffId) : "",
        exitType: exit_.exitType || "Resignation",
        noticeDate: exit_.noticeDate || "",
        lastWorkingDay: exit_.lastWorkingDay || "",
        status: exit_.status || "pending",
        notes: exit_.notes || ""
      }
    });
  };

  handleSave = async () => {
    const { form, editing, apiUnavailable } = this.state;
    if (!form.staffId || !form.exitType) {
      this.setState({ formError: "Staff member and exit type are required." });
      return;
    }
    this.setState({ saving: true, formError: null });

    const staffMember = this.state.staff.find(s => String(s.id) === form.staffId);
    const staffName = staffMember ? `${staffMember.firstName || ""} ${staffMember.lastName || ""}`.trim() : "Unknown";

    if (apiUnavailable) {
      if (editing) {
        this.setState(prev => ({
          exits: prev.exits.map(e => e.id === editing ? { ...e, ...form, staffName } : e),
          open: false, saving: false
        }));
      } else {
        this.setState(prev => ({
          exits: [{ ...form, id: Date.now(), staffName }, ...prev.exits],
          open: false, saving: false
        }));
      }
      return;
    }

    try {
      if (editing) {
        await api.put(`/hr/exits/${editing}`, form);
      } else {
        await api.post("/hr/exits", form);
      }
      this.setState({ open: false, saving: false });
      this.fetchExits();
    } catch (e) {
      // Degrade gracefully to local state
      if (editing) {
        this.setState(prev => ({
          exits: prev.exits.map(ex => ex.id === editing ? { ...ex, ...form, staffName } : ex),
          open: false, saving: false, apiUnavailable: true
        }));
      } else {
        this.setState(prev => ({
          exits: [{ ...form, id: Date.now(), staffName }, ...prev.exits],
          open: false, saving: false, apiUnavailable: true
        }));
      }
    }
  };

  getStaffName = exit_ => {
    if (exit_.staffName) return exit_.staffName;
    if (exit_.staff) return `${exit_.staff.firstName || ""} ${exit_.staff.lastName || ""}`.trim();
    const s = this.state.staff.find(x => String(x.id) === String(exit_.staffId));
    return s ? `${s.firstName || ""} ${s.lastName || ""}`.trim() : exit_.staffId || "—";
  };

  render() {
    const { classes } = this.props;
    const { exits, staff, loading, open, editing, form, saving, error, formError, apiUnavailable } = this.state;

    const columns = [
      {
        id: "staffName",
        header: "Staff Name",
        accessorFn: ex => this.getStaffName(ex)
      },
      {
        id: "exitType",
        header: "Exit Type",
        cell: info => {
          const ex = info.row.original;
          const iconName = EXIT_TYPE_ICONS[ex.exitType] || "person_off";
          return (
            <span>
              <Icon style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "4px", color: "#555" }}>{iconName}</Icon>
              {ex.exitType || "—"}
            </span>
          );
        }
      },
      { id: "noticeDate", header: "Notice Date", accessorFn: ex => ex.noticeDate || "—" },
      { id: "lastWorkingDay", header: "Last Working Day", accessorFn: ex => ex.lastWorkingDay || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const status = info.row.original.status || "pending";
          return <Pill tone={STATUS_TONES[status] || "muted"}>{status}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => (
          <Button size="sm" color="info" simple onClick={() => this.openEdit(info.row.original)}>Edit</Button>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar tabs={[]} actions={[]} />
          {apiUnavailable && (
            <div className={classes.infoMsg}>
              <Icon style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "6px" }}>info</Icon>
              Staff exits API not available — data is stored locally for this session only.
            </div>
          )}
          {error && <p className={classes.errorMsg}>{error}</p>}

          <DataTable
            title="Staff Exit Records"
            subtitle={`${exits.length} exit record${exits.length !== 1 ? "s" : ""}`}
            actions={
              <Button size="sm" color="primary" onClick={this.openAdd}>
                <Icon style={{ marginRight: "4px", fontSize: "16px" }}>add</Icon> Add Exit Record
              </Button>
            }
            columns={columns}
            data={exits}
            isLoading={loading}
            emptyState="No exit records found."
          />
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Exit Record" : "Add Exit Record"} />
          <DialogContent>
            {formError && <p className={classes.errorMsg}>{formError}</p>}
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Staff Member *</InputLabel>
                  <Select value={form.staffId} onChange={this.handleChange("staffId")}>
                    <MenuItem value="">— Select Staff —</MenuItem>
                    {staff.map(s => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {`${s.firstName || ""} ${s.lastName || ""}`.trim() || s.staffNo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Exit Type *</InputLabel>
                  <Select value={form.exitType} onChange={this.handleChange("exitType")}>
                    {EXIT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomDatePicker
                  labelText="Notice Date"
                  id="exNotice"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.noticeDate, onChange: this.handleChange("noticeDate") }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomDatePicker
                  labelText="Last Working Day"
                  id="exLWD"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.lastWorkingDay, onChange: this.handleChange("lastWorkingDay") }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Status</InputLabel>
                  <Select value={form.status} onChange={this.handleChange("status")}>
                    {EXIT_STATUSES.map(s => (
                      <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12}>
                <CustomInput
                  labelText="Notes"
                  id="exNotes"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: form.notes,
                    onChange: this.handleChange("notes"),
                    multiline: true,
                    rows: 3,
                    placeholder: "Optional notes or reason..."
                  }}
                />
              </GridItem>
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : (editing ? "Update" : "Add Record")}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

StaffExit.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(StaffExit));
